import React, { useState, useEffect } from 'react';
import { API_BASE_URL, IMAGE_BASE_URL } from '../../config';
import { UserCheck, X, Check, Loader2, AlertCircle, Edit2, Save, User } from 'lucide-react';

function fullImageUrl(path) {
  if (!path || typeof path !== 'string') return null;
  if (path.startsWith('http')) return path;
  const clean = path.replace(/^\//, '');
  return `${IMAGE_BASE_URL}${clean}`;
}

const ProviderRequests = ({ authToken }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actioning, setActioning] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const fetchRequests = () => {
    setLoading(true);
    setError('');
    fetch(`${API_BASE_URL}/admin/super/provider-requests`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => {
        if (res.status === 404) throw new Error('Backend route not found. Ensure the server is running with super admin routes (app.py + routes_super_admin.py).');
        if (!res.ok) throw new Error('Failed to load provider requests');
        return res.json();
      })
      .then((data) => {
        setRequests(data.provider_requests || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRequests();
  }, [authToken]);

  const handleUpdate = (id, updatedData) => {
    setError('');
    setActioning(id);
    // Admin can only update status; other fields are read-only
    const payload = { status: updatedData.status || 'pending' };
    fetch(`${API_BASE_URL}/admin/super/provider-requests/${id}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => {
        fetchRequests();
        setIsEditing(false);
        setSelectedRequest(null);
      })
      .catch((err) => setError(err.message || 'Update failed'))
      .finally(() => setActioning(null));
  };

  const handleApprove = (id) => {
    setActioning(id);
    fetch(`${API_BASE_URL}/admin/super/provider-requests/${id}/approve`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => fetchRequests())
      .catch((err) => setError(err.message || 'Approve failed'))
      .finally(() => setActioning(null));
  };

  const handleReject = (id) => {
    const note = window.prompt('Rejection note (optional):');
    if (note === null) return;
    setActioning(id);
    fetch(`${API_BASE_URL}/admin/super/provider-requests/${id}/reject`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ review_note: note || '' }),
    })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => fetchRequests())
      .catch((err) => setError(err.message || 'Reject failed'))
      .finally(() => setActioning(null));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Provider Requests</h2>
          <p className="text-sm text-gray-500 mt-0.5">{requests.length} request{requests.length !== 1 ? 's' : ''}</p>
        </div>
      </div>
      {error && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700 shadow-sm">
          <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError('')}
            className="ml-auto text-red-500 hover:text-red-700 font-medium text-sm"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden max-w-full">
        <div className="overflow-x-hidden overflow-y-visible">
          <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed', minWidth: 0 }}>
            <colgroup>
              <col style={{ width: '44px' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '6%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '6%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Photo</th>
                <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Artist type</th>
                <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Exp.</th>
                <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Bank</th>
                <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Portfolio</th>
                <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Details</th>
                <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created</th>
                <th className="px-2 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">Actions</th>
              </tr>
            </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {requests.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-6 py-12 text-center text-gray-500">
                  <p className="font-medium">No provider requests yet.</p>
                  <p className="text-sm mt-1">New requests will appear here.</p>
                </td>
              </tr>
            ) : (
              requests.map((req) => {
                let bd = req.bank_details;
                if (typeof bd === 'string') {
                  try { bd = JSON.parse(bd); } catch (e) { bd = null; }
                }
                const bankName = bd?.bank_name || bd?.bankName || '';
                const bankDisplay = bankName.length > 20 ? `${bankName.slice(0, 18)}…` : (bankName || '—');
                return (
                  <tr key={req.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-2 py-2 align-middle">
                      <div className="relative w-9 h-9 rounded-full overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-400" aria-hidden><User className="w-5 h-5" /></span>
                        {req.dp_image && (
                          <img
                            src={fullImageUrl(req.dp_image)}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover z-10"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-900 truncate" title={req.email}>{req.email}</td>
                    <td className="px-2 py-2 text-sm font-medium text-gray-900 truncate" title={req.first_name || req.last_name ? `${req.first_name || ''} ${req.last_name || ''}`.trim() : req.name || ''}>
                      {req.first_name || req.last_name
                        ? `${req.first_name || ''} ${req.last_name || ''}`.trim()
                        : req.name || '—'}
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-600 truncate" title={req.phone || ''}>{req.phone || '—'}</td>
                    <td className="px-2 py-2 text-sm text-gray-600 truncate" title={req.artist_type || ''}>{req.artist_type || '—'}</td>
                    <td className="px-2 py-2 text-sm text-gray-600 truncate" title={req.experience || ''}>{req.experience || '—'}</td>
                    <td className="px-2 py-2 text-sm text-gray-600 truncate" title={bankName || undefined}>
                      {bankDisplay}
                    </td>
                    <td className="px-2 py-2 text-sm">
                      {req.portfolio_url ? (
                        <a
                          href={req.portfolio_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        >
                          View
                        </a>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-2 py-2">
                      <button
                        onClick={() => setSelectedRequest(req)}
                        className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium text-sm truncate block w-full text-left"
                      >
                        View Details
                      </button>
                    </td>
                    <td className="px-2 py-2">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full capitalize ${req.status === 'pending'
                          ? 'bg-amber-100 text-amber-800'
                          : req.status === 'approved'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {req.status}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-500">
                      {req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-2 py-2 text-right bg-gray-50">
                      <div className="flex justify-end items-center gap-1.5 flex-wrap">
                        <button
                          onClick={() => {
                            setSelectedRequest(req);
                            setIsEditing(true);
                            let bd = req.bank_details;
                            if (typeof bd === 'string') try { bd = JSON.parse(bd); } catch (e) { bd = {}; }
                            setEditForm({ ...req, bank_details: bd || {} });
                          }}
                          disabled={actioning === req.id}
                          className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold rounded hover:bg-blue-100 transition-colors disabled:opacity-50"
                          title="Edit details and optionally change status"
                        >
                          <Edit2 className="h-3.5 w-3.5 mr-1" />
                          EDIT
                        </button>
                        {req.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(req.id)}
                              disabled={actioning === req.id}
                              className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                              title="Approve and onboard provider"
                            >
                              {actioning === req.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                              APPROVE
                            </button>
                            <button
                              onClick={() => handleReject(req.id)}
                              disabled={actioning === req.id}
                              className="inline-flex items-center px-3 py-1.5 bg-red-50 text-red-600 border border-red-100 text-xs font-bold rounded hover:bg-red-100 transition-colors disabled:opacity-50"
                            >
                              {actioning === req.id ? null : <X className="h-3.5 w-3.5 mr-1" />}
                              REJECT
                            </button>
                          </>
                        )}
                        {req.status === 'approved' && req.store_id && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2 py-1 rounded border border-emerald-100">
                            Onboarded ({req.store_id})
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-200">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center z-10 rounded-t-xl">
              <h3 className="text-xl font-bold text-gray-900">
                {isEditing ? 'Change status' : 'Request details'}
              </h3>
              <button
                onClick={() => { setSelectedRequest(null); setIsEditing(false); }}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">

              {isEditing ? (
                <div className="space-y-5">
                  <p className="text-sm text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
                    Only <strong>Status</strong> can be changed by admin. All other details are read-only and can only be updated by the provider in their panel.
                  </p>
                  <section className="rounded-lg border border-gray-200 bg-gray-50/50 p-4 space-y-3">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Provider details (read-only)</h4>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <span className="text-gray-500">First Name</span>
                      <span className="font-medium text-gray-900">{editForm.first_name || editForm.name?.split(' ')[0] || '—'}</span>
                      <span className="text-gray-500">Last Name</span>
                      <span className="font-medium text-gray-900">{editForm.last_name || editForm.name?.split(' ').slice(1).join(' ') || '—'}</span>
                      <span className="text-gray-500">Email</span>
                      <span className="font-medium text-gray-900 break-all">{editForm.email}</span>
                      <span className="text-gray-500">Phone</span>
                      <span className="font-medium text-gray-900">{editForm.phone || '—'}</span>
                      <span className="text-gray-500">Artist Type</span>
                      <span className="font-medium text-gray-900">{editForm.artist_type || '—'}</span>
                      <span className="text-gray-500">Experience</span>
                      <span className="font-medium text-gray-900">{editForm.experience || '—'}</span>
                      <span className="text-gray-500 col-span-2">Bio / Description</span>
                      <span className="col-span-2 text-gray-700 bg-white border border-gray-100 rounded px-3 py-2 min-h-[60px]">{editForm.description || '—'}</span>
                      {editForm.portfolio_url && (
                        <>
                          <span className="text-gray-500">Portfolio</span>
                          <a href={editForm.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline break-all">{editForm.portfolio_url}</a>
                        </>
                      )}
                    </div>
                    {editForm.bank_details && (editForm.bank_details.bank_name || editForm.bank_details.account_holder) && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Bank (read-only)</h5>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                          <span className="text-gray-500">Bank</span>
                          <span className="font-medium">{editForm.bank_details.bank_name || '—'}</span>
                          <span className="text-gray-500">Account holder</span>
                          <span className="font-medium">{editForm.bank_details.account_holder || '—'}</span>
                          <span className="text-gray-500">Account number</span>
                          <span className="font-medium font-mono text-xs">{editForm.bank_details.account_number || '—'}</span>
                          <span className="text-gray-500">IFSC</span>
                          <span className="font-medium font-mono text-xs">{editForm.bank_details.ifsc_code || '—'}</span>
                        </div>
                      </div>
                    )}
                  </section>

                  <section className="rounded-lg border-2 border-indigo-200 bg-indigo-50/50 p-4">
                    <label className="block text-xs font-bold text-indigo-700 uppercase tracking-wider mb-2">Status (editable)</label>
                    <select
                      className="w-full border border-indigo-200 rounded-lg px-4 py-2.5 text-sm font-medium bg-white text-gray-900 shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={editForm.status || 'pending'}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    <p className="text-xs text-indigo-600 mt-2">For &quot;Approved&quot;, use the Approve button in the table if a store is not created yet.</p>
                  </section>
                </div>
              ) : (
                <div className="space-y-5">
                  <section>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Profile Identity</h4>
                    <div className="flex gap-4 mb-4">
                      <div className="flex gap-4 items-start w-full">
                        <div className="relative w-16 h-16 rounded-full overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0">
                          <div className="absolute inset-0 flex items-center justify-center text-gray-400" aria-hidden>
                            <User className="w-8 h-8" />
                          </div>
                          {selectedRequest.dp_image && (
                            <img
                              src={fullImageUrl(selectedRequest.dp_image)}
                              alt="Profile"
                              className="relative z-10 w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                        </div>
                        {selectedRequest.banner_image && (
                          <div className="flex-1 min-w-0 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                            <img
                              src={fullImageUrl(selectedRequest.banner_image)}
                              alt="Banner"
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-2 text-sm">
                      <span className="text-gray-500">First Name:</span>
                      <span className="font-medium">{selectedRequest.first_name || selectedRequest.name?.split(' ')[0] || '—'}</span>
                      <span className="text-gray-500">Last Name:</span>
                      <span className="font-medium">{selectedRequest.last_name || selectedRequest.name?.split(' ').slice(1).join(' ') || '—'}</span>
                      <span className="text-gray-500">Email Address:</span>
                      <span className="font-medium text-indigo-600">{selectedRequest.email}</span>
                      <span className="text-gray-500">Phone:</span>
                      <span className="font-medium">{selectedRequest.phone || '—'}</span>
                      <span className="text-gray-500">Artist Type:</span>
                      <span className="font-medium">{selectedRequest.artist_type || '—'}</span>
                      <span className="text-gray-500">Experience:</span>
                      <span className="font-medium">{selectedRequest.experience || '—'}</span>
                    </div>
                  </section>

                  <section>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Description / Bio</h4>
                    <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border whitespace-pre-wrap leading-relaxed">
                      {selectedRequest.description || 'No description provided.'}
                    </div>
                  </section>

                  <section>
                    <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-2">Bank Account Details</h4>
                    {(() => {
                      let bd = selectedRequest.bank_details;
                      if (typeof bd === 'string') {
                        try { bd = JSON.parse(bd); } catch (e) { bd = null; }
                      }
                      if (!bd) return (
                        <div className="p-3 bg-red-50 text-red-600 rounded text-sm border border-red-100">
                          Missing bank account details.
                        </div>
                      );

                      return (
                        <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100 shadow-sm space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-indigo-600">Bank Name</span>
                            <span className="font-bold">{bd.bank_name || bd.bankName || '—'}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-indigo-600">Account Holder</span>
                            <span className="font-bold">{bd.account_holder || bd.accountHolderName || '—'}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-indigo-600">Account Number</span>
                            <span className="font-bold font-mono tracking-tighter">{bd.account_number || bd.accountNumber || '—'}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-indigo-600">IFSC Code</span>
                            <span className="font-bold font-mono text-indigo-800">{bd.ifsc_code || bd.ifscCode || '—'}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </section>

                  {selectedRequest.portfolio_url && (
                    <section>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Portfolio</h4>
                      <a
                        href={selectedRequest.portfolio_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm break-all flex items-center group"
                      >
                        <span className="group-hover:underline">{selectedRequest.portfolio_url}</span>
                      </a>
                    </section>
                  )}
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={() => { setSelectedRequest(null); setIsEditing(false); }}
                  className="px-5 py-2.5 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>

                {isEditing ? (
                  <button
                    onClick={() => handleUpdate(selectedRequest.id, editForm)}
                    disabled={actioning === selectedRequest.id}
                    className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 shadow-sm transition-colors inline-flex items-center gap-2"
                  >
                    {actioning === selectedRequest.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save status
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setIsEditing(true);
                        let bd = selectedRequest.bank_details;
                        if (typeof bd === 'string') try { bd = JSON.parse(bd); } catch (e) { bd = {}; }
                        setEditForm({ ...selectedRequest, bank_details: bd || {} });
                      }}
                      className="px-5 py-2.5 bg-white border border-indigo-200 text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition-colors inline-flex items-center gap-2"
                    >
                      <Edit2 className="h-4 w-4" />
                      Change status
                    </button>
                    {selectedRequest.status === 'pending' && (
                      <>
                        <button
                          onClick={() => { handleReject(selectedRequest.id); setSelectedRequest(null); }}
                          className="px-6 py-2 bg-white border border-red-200 text-red-600 rounded-md font-medium hover:bg-red-50 transition-colors"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => { handleApprove(selectedRequest.id); setSelectedRequest(null); }}
                          className="px-6 py-2 bg-green-600 text-white rounded-md font-medium hover:bg-green-700 shadow-md transition-colors"
                        >
                          Approve & Onboard
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderRequests;
